import type { ReactNode } from 'react'
import { useEffect, useId, useState } from 'react'

/** Small shared building blocks. Every list view uses the same three states. */

export function Spinner({ label = 'Wird geladen' }: { label?: string }) {
  return (
    <span role="status" aria-live="polite" className="inline-flex items-center gap-2 text-sm text-paper-500">
      <span
        aria-hidden
        className="h-3 w-3 animate-spin rounded-full border-2 border-paper-300 border-t-ink-600"
      />
      {label}…
    </span>
  )
}

export function LoadingBlock({ label }: { label?: string }) {
  return (
    <div className="card flex items-center justify-center py-10">
      <Spinner label={label} />
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="card flex flex-col items-center gap-2 py-12 text-center">
      <p className="font-display text-lg">{title}</p>
      {description ? <p className="max-w-prose text-sm text-paper-500">{description}</p> : null}
      {action}
    </div>
  )
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message =
    error && typeof error === 'object' && 'error' in error
      ? ((error as { error?: { message?: string } }).error?.message ?? 'Unbekannter Fehler')
      : error instanceof Error
        ? error.message
        : 'Unbekannter Fehler'
  return (
    <div
      role="alert"
      className="card border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
    >
      <p className="font-medium">Das hat nicht geklappt.</p>
      <p className="mt-1 text-sm">{message}</p>
      {onRetry ? (
        <button type="button" className="btn-secondary mt-3" onClick={onRetry}>
          Erneut versuchen
        </button>
      ) : null}
    </div>
  )
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: string
  error?: string
  children: (props: { id: string; 'aria-describedby': string | undefined }) => ReactNode
}) {
  const id = useId()
  const hintId = hint || error ? `${id}-hint` : undefined
  return (
    <div className="mb-4">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      {children({ id, 'aria-describedby': hintId })}
      {error ? (
        <p id={hintId} className="mt-1 text-xs text-red-700 dark:text-red-300">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="hint">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: 'neutral' | 'accent' | 'warn' | 'danger' | 'ok'
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-paper-100 text-paper-700 dark:bg-paper-800 dark:text-paper-200',
    accent: 'bg-ink-100 text-ink-800 dark:bg-ink-800 dark:text-ink-50',
    warn: 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100',
    danger: 'bg-red-100 text-red-900 dark:bg-red-900/50 dark:text-red-100',
    ok: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-100',
  }
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs ${tones[tone]}`}>
      {children}
    </span>
  )
}

export function Section({
  title,
  description,
  actions,
  children,
}: {
  title: string
  description?: string
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="mb-8">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-xl">{title}</h2>
          {description ? <p className="mt-0.5 text-sm text-paper-500">{description}</p> : null}
        </div>
        {actions}
      </div>
      {children}
    </section>
  )
}

export function useDarkMode(): [boolean, (value: boolean) => void] {
  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem('backoffice.theme')
    if (stored) return stored === 'dark'
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark)
    localStorage.setItem('backoffice.theme', dark ? 'dark' : 'light')
  }, [dark])
  return [dark, setDark]
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatAmount(value: number | string | null | undefined, currency?: string | null): string {
  if (value === null || value === undefined) return '—'
  const numeric = typeof value === 'string' ? Number.parseFloat(value) : value
  if (Number.isNaN(numeric)) return '—'
  return numeric.toLocaleString('de-DE', {
    style: 'currency',
    currency: currency || 'EUR',
  })
}

export function formatBytes(value: number): string {
  if (!value) return '0 B'
  const units = ['B', 'kB', 'MB', 'GB', 'TB']
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
  return `${(value / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`
}
